import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Calendar, User, CreditCard as Edit2, Trash2, TrendingUp, TrendingDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase, WeeklyLedger, LedgerEntry, User as UserType, EntryType } from '../lib/supabase';
import { formatDate, formatCurrency } from '../lib/dateFormat';
import { translateEntryTypeName } from '../lib/translateEntryType';
import { useLanguage } from '../contexts/LanguageContext';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';

type LedgerWithDetails = WeeklyLedger & {
  user: UserType;
  entries: (LedgerEntry & { entry_type: EntryType })[];
  total: number;
};

const ITEMS_PER_PAGE = 5;

type LedgerProps = {
  onNewLedger?: () => void;
  onEditLedger?: (ledger: LedgerWithDetails) => void;
};

export default function Ledger({ onNewLedger, onEditLedger }: LedgerProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [ledgers, setLedgers] = useState<LedgerWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ledgerToDelete, setLedgerToDelete] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadLedgers();
  }, []);

  const loadLedgers = async () => {
    setLoading(true);
    const { data: ledgersData, error: ledgersError } = await supabase
      .from('weekly_ledgers')
      .select('*')
      .order('date', { ascending: false });

    if (ledgersError || !ledgersData) {
      setLoading(false);
      return;
    }

    const { data: usersData } = await supabase.from('profiles').select('*');
    const { data: entriesData } = await supabase
      .from('ledger_entries')
      .select('*, entry_type:entry_types(*)');

    const ledgersWithDetails = ledgersData.map(ledger => {
      const user = usersData?.find(u => u.id === ledger.created_by);
      const entries = entriesData?.filter(e => e.weekly_ledger_id === ledger.id) || [];
      const total = entries.reduce((sum, entry) => {
        const amount = Number(entry.amount);
        const entryType = entry.entry_type as unknown as EntryType;
        if (entryType?.entry_category === 'expense') {
          return sum - amount;
        }
        return sum + amount;
      }, 0);

      return {
        ...ledger,
        user: user!,
        entries,
        total
      };
    });

    setLedgers(ledgersWithDetails);
    setLoading(false);
  };

  const handleDelete = (id: string) => {
    setLedgerToDelete(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!ledgerToDelete) return;

    const { error } = await supabase
      .from('weekly_ledgers')
      .delete()
      .eq('id', ledgerToDelete);

    if (!error) {
      loadLedgers();
    }
    setLedgerToDelete(null);
  };

  const handleEdit = (ledger: LedgerWithDetails) => {
    if (onEditLedger) {
      onEditLedger(ledger);
    }
  };

  const handleNewLedger = () => {
    if (onNewLedger) {
      onNewLedger();
    }
  };

  const filteredLedgers = ledgers.filter(ledger =>
    ledger.user?.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ledger.date.includes(searchTerm) ||
    ledger.notes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredLedgers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedLedgers = searchTerm ? filteredLedgers : filteredLedgers.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 7;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">{t('nav.ledger')}</h1>
          <p className="text-slate-600 mt-1">{t('dashboard.subtitle')}</p>
        </div>
        <button
          onClick={handleNewLedger}
          className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors shadow-sm"
        >
          <Plus className="w-5 h-5" />
          {t('dashboard.newWeek')}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="mb-6">
          <input
            type="text"
            placeholder={t('dashboard.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
          />
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-600">{t('dashboard.loading')}</div>
        ) : paginatedLedgers.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">{t('dashboard.noLedgers')}</p>
            <p className="text-sm text-slate-500 mt-1">{t('dashboard.noLedgersSubtext')}</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {paginatedLedgers.map((ledger) => (
                <div
                  key={ledger.id}
                  className="border border-slate-200 rounded-lg p-5 hover:border-emerald-300 hover:shadow-md transition-all"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 text-lg">
                          {formatDate(ledger.date, "EEEE, d 'de' MMMM 'de' yyyy", language)}
                        </h3>
                        <p className="text-sm text-slate-500 flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {ledger.user?.full_name || ledger.user?.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right mr-4">
                        <p className={`text-2xl font-bold ${
                          ledger.total >= 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>{formatCurrency(ledger.total)}</p>
                        <p className="text-xs text-slate-500">{t('dashboard.netTotal')}</p>
                      </div>
                      <button
                        onClick={() => handleEdit(ledger)}
                        className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(ledger.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-200">
                    {ledger.entries.map((entry) => (
                      <div key={entry.id} className="rounded-lg p-3 border border-slate-200">
                        <p className="text-xs mb-1 flex items-center gap-1">
                          {entry.entry_type.entry_category === 'income' ? (
                            <TrendingUp className="w-3 h-3 text-emerald-600" />
                          ) : (
                            <TrendingDown className="w-3 h-3 text-red-600" />
                          )}
                          <span className="text-slate-700">
                            {translateEntryTypeName(entry.entry_type.name, language)}
                          </span>
                        </p>
                        <p className={`font-semibold ${
                          entry.entry_type.entry_category === 'income' ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {entry.entry_type.entry_category === 'expense' ? '-' : ''}{formatCurrency(Number(entry.amount))}
                        </p>
                      </div>
                    ))}
                  </div>

                  {ledger.notes && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-sm text-slate-600">{ledger.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!searchTerm && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6 pt-6 border-t border-slate-200">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {getPageNumbers().map((page, index) => (
                  typeof page === 'number' ? (
                    <button
                      key={index}
                      onClick={() => setCurrentPage(page)}
                      className={`min-w-[40px] h-10 rounded-lg font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-emerald-600 text-white'
                          : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {page}
                    </button>
                  ) : (
                    <span key={index} className="px-2 text-slate-400">
                      {page}
                    </span>
                  )
                ))}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}

            {searchTerm && (
              <div className="mt-6 pt-6 border-t border-slate-200 text-center text-sm text-slate-600">
                {filteredLedgers.length} {filteredLedgers.length === 1 ? 'result' : 'results'} found
              </div>
            )}
          </>
        )}
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title={t('deleteModal.deleteWeeklyLedger')}
        message={t('deleteModal.deleteWeeklyLedgerMessage')}
      />
    </div>
  );
}